import React, { useEffect, useRef, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Snackbar, Alert, CircularProgress,
  Paper, Stack, TextField, InputAdornment, Avatar, Tooltip, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { marketingApi } from '../api/apiClient';
import ConfirmDialog from '../common/ConfirmDialog';
import PageShell from '../common/PageShell';
import StatCard from '../common/StatCard';
import EmptyState from '../common/EmptyState';

const colorFor = (name = '') => {
  const palette = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669', '#0891b2', '#4f46e5'];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return palette[sum % palette.length];
};

const MarketingList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const fetchedRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await marketingApi.getAll();
      setRows(res.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchData();
  }, []);

  const handleDelete = async () => {
    try {
      await marketingApi.delete(deleteId);
      setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setDeleteId(null);
    }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.active).length,
    inactive: rows.filter((r) => !r.active).length,
    withExec: rows.filter((r) => r.salesExecutiveName).length,
  }), [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const matchesSearch =
        !search ||
        [r.companyName, r.contactNumber, r.address, r.salesExecutiveName]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && r.active) ||
        (statusFilter === 'inactive' && !r.active);
      return matchesSearch && matchesStatus;
    });
  }, [rows, search, statusFilter]);

  return (
    <PageShell
      icon={<StorefrontIcon />}
      title="Marketing Records"
      subtitle="Manage companies, contacts and their assigned sales executives"
      breadcrumbs={[{ label: 'Dashboard', to: '/' }, { label: 'Marketing' }]}
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/marketing/new')}
          sx={{
            bgcolor: '#fff',
            color: '#1e3a8a',
            fontWeight: 700,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          }}
        >
          Add Marketing
        </Button>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total Records" value={stats.total} icon={<StorefrontIcon />} color="#2563eb" />
        <StatCard label="Active" value={stats.active} icon={<CheckCircleIcon />} color="#059669" />
        <StatCard label="Inactive" value={stats.inactive} icon={<BlockIcon />} color="#64748b" />
        <StatCard label="Assigned" value={stats.withExec} icon={<PeopleIcon />} color="#7c3aed" />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          size="small"
          placeholder="Search company, contact, executive..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active only</MenuItem>
          <MenuItem value="inactive">Inactive only</MenuItem>
        </TextField>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<StorefrontIcon />}
            title={rows.length === 0 ? 'No marketing records yet' : 'No matches'}
            subtitle={
              rows.length === 0
                ? 'Create your first marketing record to start tracking companies and contacts.'
                : 'Try adjusting your search or filter.'
            }
            actionLabel={rows.length === 0 ? 'Add Marketing' : undefined}
            onAction={() => navigate('/marketing/new')}
          />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
        >
          <TableContainer>
            <Table sx={{ minWidth: isMobile ? 0 : 800 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: 'grey.50',
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableCell>Company</TableCell>
                  <TableCell>Contact</TableCell>
                  {!isMobile && <TableCell>Address</TableCell>}
                  <TableCell>Sales Executive</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                    onClick={() => navigate(`/marketing/view/${row.id}`)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: colorFor(row.companyName),
                            width: 38,
                            height: 38,
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          {(row.companyName || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {row.companyName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID #{row.id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.contactNumber || '—'}</Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.address || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {row.salesExecutiveName ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: 'primary.main',
                              fontSize: 12,
                            }}
                          >
                            {row.salesExecutiveName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{row.salesExecutiveName}</Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.active ? 'Active' : 'Inactive'}
                        color={row.active ? 'success' : 'default'}
                        size="small"
                        variant={row.active ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/marketing/view/${row.id}`);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/marketing/edit/${row.id}`);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteId(row.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Delete Marketing Record"
        message="This will also remove all associated follow-ups. This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default MarketingList;